/**
* @summary - Calibration Parent Page
* @author Yuvasri S
* @since 2024-12-17
*/
import React, { useState, useRef, useEffect } from "react";
import { Box, Grid, Button } from "@mui/material";
import CalibrationCertificateForm from "./CalibrationCertificateForm";
import AssetSubformgrid from "./AssetSubformgrid";
import Contractorsubformgrid from "./Contractorsubformgrid";
import Employeesubformgrid from "./Employeesubformgrid";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDialog from "../commoncomponents/CustomDialog";

const CalibrationCertificateCreate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const editMode = location?.state?.editmode;
    const viewMode = location?.state?.viewmode;
    const calibrationCreateRef = useRef(null);
    const calibrationSubFormGridRef = useRef(null);

    const [selectedCalibrationCategory, setSelectedCalibrationCategory] = useState(
        editMode || viewMode ? location?.state?.categoryName : null
    );

    const [selectedService, setSelectedService] = useState(editMode || viewMode ? location?.state?.service : null);

    const [dialogContent, setDialogContent] = useState("");
    const [openDialog, setOpenDialog] = useState(false);
    const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(false);
    const [rowLength, setRowLength] = useState(0);

    const handleCalibrationCategoryChange = (category) => {
        if (!editMode && !viewMode) {
            if (category && category.value !== undefined) {
                setSelectedCalibrationCategory(category.value);
            } else {
                setSelectedCalibrationCategory(null); // Set to null or handle as needed
            }
        }
    };

    const handleServiceChange = (service) => {
        if (!editMode && !viewMode) {
            setSelectedService(service.value);
        }
    };


    useEffect(() => {
        if (editMode && location?.state?.categoryName) {
            setSelectedCalibrationCategory(location?.state?.categoryName);
        }
    }, [editMode, viewMode, location?.state?.categoryName]);


    const handleSave = async () => {
        let headerId;
        try {
            let isEditFormSaved = false;
            let isSubFormSaved = false;
            if (editMode) {
                if (calibrationCreateRef.current.hospital === "Hospital" ? calibrationCreateRef.current : calibrationCreateRef.current && rowLength > 0) {
                    const formResponse = await calibrationCreateRef.current.handleSubmit();
                    if (formResponse && formResponse.id) {
                        headerId = formResponse.id;
                        if (calibrationSubFormGridRef.current && headerId) {
                            await calibrationSubFormGridRef.current.handleSubFormSubmit(headerId);
                        }
                        setDialogContent("Data updated successfully.");
                        setOpenDialog(true);
                    } else if (formResponse?.status === 400) {
                        setDialogContent(formResponse.response?.data?.detail || "Calibration No Already Exists.");
                        setOpenDialog(true);
                        return; // Exit on error
                    } else {
                        throw new Error("Form submission failed or returned undefined response");
                    }
                }

            } else {

                const validation = await calibrationCreateRef.current.validateFields();
                if (validation === true) {
                    if (calibrationCreateRef.current.hospital.value === "Hospital" ? calibrationCreateRef.current : calibrationCreateRef.current && rowLength > 0) {
                        const formResponse = await calibrationCreateRef.current.handleSubmit();
                        if (formResponse && formResponse.id) {
                            headerId = formResponse.id;
                            isEditFormSaved = true;
                        } else if (formResponse?.status === 400) {
                            setDialogContent(formResponse.response?.data?.detail || "Calibration No already exists.");
                            setOpenDialog(true);
                            return; // Exit on error
                        } else {
                            throw new Error("Form submission failed or returned undefined response");
                        }
                    }
                    if (calibrationCreateRef.current.hospital.value !== "Hospital") {
                        if (calibrationSubFormGridRef.current && headerId) {
                            const subForm = await calibrationSubFormGridRef.current.handleSubFormSubmit(headerId);
                            if (subForm[0]?.id) {
                                isSubFormSaved = true;
                            } else {
                                throw new Error("Grid submission failed or returned undefined response");
                            }
                        }
                    }
                    else {
                        isSubFormSaved = true;
                    }

                    if (isEditFormSaved && isSubFormSaved) {
                        setDialogContent("Data saved successfully.");
                        setOpenDialog(true);
                    }
                }
            }

        } catch (error) {
            console.error(error); // Handle error as needed
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleCancel = () => {
        if (viewMode) {
            navigate(-1, { replace: true });
            return;
        }

        const hasChanges = calibrationCreateRef.current?.hasUnsavedChanges?.();
        if (!hasChanges) {
            navigate(-1, { replace: true });
        } else {
            setUnsavedChangesDialog(true);
        }
    };

    const confirmCancel = () => {
        setUnsavedChangesDialog(false);
        navigate(-1, { replace: true });
    };

    const cancelCloseDialog = () => {
        setUnsavedChangesDialog(false);
    };

    const renderSubformGrid = () => {
        switch (selectedCalibrationCategory) {
            case "Asset":
                return <AssetSubformgrid ref={calibrationSubFormGridRef} service={selectedService} calbirationCategory={selectedCalibrationCategory} setRowLength={(item) => setRowLength(item)} />;
            case "Employee":
                return <Employeesubformgrid ref={calibrationSubFormGridRef} service={selectedService} calbirationCategory={selectedCalibrationCategory} setRowLength={(item) => setRowLength(item)} />;
            case "Contractor":
                return <Contractorsubformgrid ref={calibrationSubFormGridRef} service={selectedService} calbirationCategory={selectedCalibrationCategory} setRowLength={(item) => setRowLength(item)} />;
            default:
                return null;
        }
    };
    return (
        <>
            <Box mb={2} />
            <CalibrationCertificateForm ref={calibrationCreateRef} onCalibrationCategoryChange={handleCalibrationCategoryChange} onServiceChange={handleServiceChange} />
            <Box mb={2} />
            {renderSubformGrid()}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                {!viewMode && (
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ m: 1 }}
                        onClick={handleSave}
                    >
                        {editMode ? "Update" : "Save"}
                    </Button>
                )}

                <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                    Back
                </Button>
            </Box>
            <CustomDialog
                open={openDialog}
                onClose={handleCloseDialog}
                content={dialogContent}
                confirmBtn="OK"
                onConfirm={handleCloseDialog}
            />
            <CustomDialog
                open={unsavedChangesDialog}
                onClose={cancelCloseDialog}
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={confirmCancel}
                onCancel={cancelCloseDialog}
            />
        </>
    );
};

export default CalibrationCertificateCreate;
